import React, { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import log from "loglevel";
import { Button, Modal } from "react-bootstrap";
import ReactTooltip from "react-tooltip";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addAnalysisAction,
  modifyAnalysisAction,
  deleteAnalysisAction,
} from "store/actions/actionCreators";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import { validAnalysisName, isStringValid } from "util/formValidators";
import {
  Analysis,
  Account,
  CreateAnalysisRequestType,
  ModifyAnalysisRequestType,
  DeleteAnalysisRequestType,
} from "types";
import { generateAccountDropdownItems } from "util/dashboardUtil";
import AnalysesTable from "./AnalysesTable";
import genStyles from "styles/General.module.scss";
import styles from "components/dashboard/Dashboard.module.scss";
import * as r from "routes";
import { Form } from "@avlino-inc/alenza-react";

type CreateAnalysisFormType = {
  name: string | undefined;
  account_id: number | undefined;
  description: string | undefined;
};

type ModifyAnalysisFormType = {
  name: string | undefined;
  description: string | undefined;
};

const AnalysesCard = ({
  analyses,
  accounts,
  userIsAdmin,
  showCreateModal,
  setShowCreateModal,
  showSearch,
}: {
  analyses: Analysis[];
  accounts: Account[];
  userIsAdmin: boolean;
  showCreateModal: boolean;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSearch: boolean;
}) => {
  const dispatch = useDispatch();

  const [selectedAnalysis, setSelectedAnalysis] = useState<Analysis>();
  const [createAnalysis, setCreateAnalysis] = useState<CreateAnalysisFormType>({
    name: undefined,
    account_id: undefined,
    description: undefined,
  });
  const [modifyAnalysis, setModifyAnalysis] = useState<ModifyAnalysisFormType>({
    name: undefined,
    description: undefined,
  });
  const [deleteAnalysisConfirm, setDeleteAnalysisConfirm] = useState<string>();
  // eslint-disable-next-line
  const [isCreatingAnalysis, setIsCreatingAnalysis] = useState(false);
  // eslint-disable-next-line
  const [isModifyingAnalysis, setIsModifyingAnalysis] = useState(false);
  // eslint-disable-next-line
  const [isDeletingAnalysis, setIsDeletingAnalysis] = useState(false);
  // const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const onHandleCreateAnalysisClick = (event: any) => {
    event.preventDefault();
    log.info("Attempting to CREATE Analysis...");
    if (
      createAnalysis?.name &&
      createAnalysis?.account_id &&
      createAnalysis?.description &&
      validAnalysisName(createAnalysis?.name)
    ) {
      const request: CreateAnalysisRequestType = {
        body: {
          name: createAnalysis.name,
          description: createAnalysis.description,
          account_id: createAnalysis.account_id,
        },
      };
      log.info("-- Dispatching addAnalysisAction()...");
      dispatch(addAnalysisAction(request));
      setIsCreatingAnalysis(false);
      setShowCreateModal(false);
      setCreateAnalysis({
        name: undefined,
        account_id: undefined,
        description: undefined,
      }); // clear
    } else {
      log.info("One or more fields NOT VALID!");
      log.info("CreateAnalysisFormType: " + JSON.stringify(createAnalysis));
    }
  };

  const onHandleModifyAnalysisClick = (event: any) => {
    event.preventDefault();
    log.info("Attempting to MODIFY Analysis...");
    if (
      selectedAnalysis &&
      selectedAnalysis.id &&
      modifyAnalysis?.name &&
      modifyAnalysis?.description &&
      validAnalysisName(modifyAnalysis?.name)
    ) {
      log.info("All fields valid! Modifying...");
      const request: ModifyAnalysisRequestType = {
        id: selectedAnalysis.id,
        body: {
          name: modifyAnalysis?.name,
          description: modifyAnalysis?.description,
        },
      };
      log.info("-- Dispatching modifyAnalysisAction()...");
      dispatch(modifyAnalysisAction(request));
      setIsModifyingAnalysis(false);
      setShowSettingsModal(false);
      setSelectedAnalysis(undefined); // clear
      setModifyAnalysis({ name: undefined, description: undefined }); // clear
    } else {
      log.info("One or more fields NOT VALID!");
    }
  };

  const confirmAnalysisDelete = () => {
    return selectedAnalysis && selectedAnalysis.name === deleteAnalysisConfirm;
  };

  const onHandleDeleteAnalysisClick = (event: any) => {
    event.preventDefault();
    if (selectedAnalysis && confirmAnalysisDelete()) {
      const request: DeleteAnalysisRequestType = {
        id: selectedAnalysis.id,
      };
      log.info("-- Dispatching deleteAccountAction()...");
      dispatch(deleteAnalysisAction(request));
      setIsDeletingAnalysis(false);
      setShowSettingsModal(false);
      setSelectedAnalysis(undefined); // clear
      setDeleteAnalysisConfirm(""); //clear
    }
  };

  const onSelectAccountFromDropdown = (accountItem: any) => {
    if (accountItem) {
      log.info(
        "Create Analysis Account Dropdown Item Selected! : " +
          JSON.stringify(accountItem)
      );
      setCreateAnalysis({
        name: createAnalysis?.name,
        account_id: accountItem.id,
        description: createAnalysis?.description,
      });
    }
  };

  const analysesRowEvents = {
    onClick: (e: any, row: Analysis, rowIndex: number) => {
      log.info("Row Index: " + rowIndex);
      log.info("Row: " + JSON.stringify(row));
      setSelectedAnalysis(row);
      setModifyAnalysis({
        name: row.name,
        description: row.description,
      });
      setShowSettingsModal(true);
    },
  };

  const analysesCard = () => {
    return (
      <>
        {analyses && accounts ? (
          <AnalysesTable
            analyses={analyses}
            rowEvents={analysesRowEvents}
            showAccount={true}
            showSearch={showSearch}
          />
        ) : null}
      </>
    );
  };

  const [wasNameTouched, setWasNameTouched] = useState(false);
  const [wasDescriptionTouched, setWasDescriptionTouched] = useState(false);

  const inputNameValidity = useMemo(() => {
    const isValid = isStringValid(createAnalysis?.name, validAnalysisName);
    const isInvalid = wasNameTouched && !isValid;
    if (isValid) return "valid";
    else if (isInvalid) return "invalid";
    else return undefined;
  }, [wasNameTouched, createAnalysis?.name]);

  const inputDescriptionValidity = useMemo(() => {
    const isValid = (createAnalysis?.description?.length || 0) > 0;
    const isInvalid = wasDescriptionTouched && !isValid;
    if (isValid) return "valid";
    else if (isInvalid) return "invalid";
    else return undefined;
  }, [wasDescriptionTouched, createAnalysis?.description]);

  const selectAccountOptions = useMemo(
    () => generateAccountDropdownItems(accounts),
    [accounts]
  );
  const selectedAccountOption = useMemo(
    () =>
      selectAccountOptions?.find(
        (option) => option.id === createAnalysis?.account_id
      ),
    [selectAccountOptions, createAnalysis?.account_id]
  );

  const isFormValid = useMemo(
    () =>
      [inputNameValidity, inputDescriptionValidity].every(
        (value) => value === "valid"
      ) && selectedAccountOption !== undefined,
    [inputNameValidity, inputDescriptionValidity, selectedAccountOption]
  );

  const getValidity = () => {
    if (
      isStringValid(modifyAnalysis?.name, validAnalysisName) === true &&
      modifyAnalysis?.name !== selectedAnalysis?.name
    ) {
      return "valid";
    } else if (
      isStringValid(modifyAnalysis?.name, validAnalysisName) === false ||
      modifyAnalysis?.name === selectedAnalysis?.name
    ) {
      return "invalid";
    }
  };

  const createAnalysisModal = () => {
    return (
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setCreateAnalysis({
              name: undefined,
              account_id: undefined,
              description: undefined,
            });
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Create Analysis <FontAwesomeIcon icon={["fal", "analytics"]} />
            </Modal.Title>
          </Modal.Header>
          <form name="create-analysis-form">
            <Modal.Body>
              <div className="w-100" style={{ textAlign: "center" }}>
                <Form.Group controlId="create_analysis_name">
                  <Form.Input
                    type="text"
                    placeholder="Analysis Name"
                    value={createAnalysis?.name}
                    status={inputNameValidity}
                    onBlur={() => setWasNameTouched(true)}
                    onChange={(e: any) => {
                      if (e.target instanceof HTMLInputElement) {
                        const value = e.target.value;
                        setCreateAnalysis((state) => ({
                          ...state,
                          name: value,
                        }));
                      }
                    }}
                  />
                  <Form.Input.Feedback
                    renderAssistiveText={() =>
                      inputNameValidity === "invalid" ? (
                        <>
                          Name must be between 5-19 characters.
                          <br />
                          Name can only contain alphanumeric characters.
                        </>
                      ) : null
                    }
                  />
                </Form.Group>
                <Form.Group controlId="analysis_description_create">
                  <Form.Input
                    type="text"
                    placeholder="Please provide a brief description of this Analysis"
                    rows={3}
                    columns={50}
                    maxLength={150}
                    value={createAnalysis?.description}
                    style={{ resize: "none" }}
                    status={inputDescriptionValidity}
                    onBlur={() => setWasDescriptionTouched(true)}
                    onChange={(e: any) => {
                      if (e.target instanceof HTMLTextAreaElement) {
                        const value = e.target.value;
                        setCreateAnalysis((state) => ({
                          ...state,
                          description: value,
                        }));
                      }
                    }}
                  />
                  <Form.Input.Feedback
                    renderAssistiveText={() =>
                      inputDescriptionValidity === "invalid" ? (
                        <>Description cannot be blank.</>
                      ) : null
                    }
                  />
                </Form.Group>
                <p>
                  <small>
                    {`${
                      150 -
                      (createAnalysis && createAnalysis.description
                        ? createAnalysis.description.length
                        : 0)
                    } chars left`}
                  </small>
                </p>
                <Select
                  value={selectedAccountOption}
                  options={selectAccountOptions}
                  placeholder={"Select Account..."}
                  isClearable={false}
                  onChange={onSelectAccountFromDropdown}
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <input
                type="submit"
                value="Create"
                className={[
                  genStyles["alenza__btn-create__mini"],
                  "float-right",
                ].join(" ")}
                disabled={!isFormValid}
                onClick={(e: any) => {
                  log.info("Create Analysis Button Clicked!");
                  setIsCreatingAnalysis(true);
                  onHandleCreateAnalysisClick(e);
                }}
              />
            </Modal.Footer>
          </form>
        </Modal>
      </>
    );
  };

  const analysisSettingsModal = () => {
    return (
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showSettingsModal}
          onHide={() => {
            setShowSettingsModal(false);
            setModifyAnalysis({ name: undefined, description: undefined });
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Analysis Settings
              <FontAwesomeIcon
                style={{ marginLeft: "5px" }}
                icon={["fal", "cog"]}
              />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              {selectedAnalysis ? (
                <Link
                  style={{ marginRight: "10px" }}
                  to={r.ACCOUNT_ANALYSIS(
                    selectedAnalysis.account_name.toLowerCase(),
                    selectedAnalysis.name.toLowerCase()
                  )}
                >
                  <button
                    type="submit"
                    className={genStyles["alenza__btn-primary__mini"]}
                  >
                    Go to {selectedAnalysis.name}
                    <FontAwesomeIcon icon={["fal", "long-arrow-right"]} />
                  </button>
                </Link>
              ) : null}
            </div>
            {userIsAdmin ? (
              <div style={{ marginTop: "4px" }}>
                {selectedAnalysis ? (
                  <a
                    href={`https://app.powerbi.com/groups/${selectedAnalysis.pbi_workspace_id}/groupWelcome`}
                    target="_blank"
                  >
                    Visit workspace on powerbi.com
                  </a>
                ) : null}
              </div>
            ) : null}
          </Modal.Body>
          <>
            <form name="modify-form">
              <Modal.Body style={{ paddingTop: "8px" }}>
                <h5>Rename</h5>

                <Form.Group controlId="analysis_name_edit">
                  <Form.Input
                    type="text"
                    placeholder="Analysis Name"
                    style={{ marginBottom: "30px" }}
                    value={modifyAnalysis?.name || ""}
                    status={getValidity()}
                    onBlur={() => setWasNameTouched(true)}
                    onChange={(e: any) => {
                      if (e.target instanceof HTMLInputElement) {
                        const value = e.target.value;
                        setModifyAnalysis((state) => ({
                          ...state,
                          name: value,
                        }));
                      }
                    }}
                  />
                </Form.Group>

                <h5>Edit Description</h5>

                <Form.Group controlId="analysis_description_edit">
                  <Form.Input
                    type="text"
                    rows={3}
                    columns={50}
                    placeholder="Please provide a brief description of this Analysis"
                    value={modifyAnalysis?.description}
                    style={{ resize: "none" }}
                    onChange={(e: any) => {
                      if (e.target instanceof HTMLTextAreaElement) {
                        const value = e.target.value;
                        setModifyAnalysis((state) => ({
                          ...state,
                          description: value,
                        }));
                      }
                    }}
                  />
                </Form.Group>
                <p>{`${
                  150 -
                  (modifyAnalysis && modifyAnalysis.description
                    ? modifyAnalysis.description.length
                    : 0)
                } chars left`}</p>
                <input
                  type="submit"
                  value="Save"
                  className={[
                    genStyles["alenza__btn-create__mini"],
                    "float-right",
                  ].join(" ")}
                  disabled={
                    !validAnalysisName(modifyAnalysis?.name) ||
                    selectedAnalysis?.name === modifyAnalysis?.name
                  }
                  onClick={(e: any) => {
                    setIsModifyingAnalysis(true);
                    onHandleModifyAnalysisClick(e);
                  }}
                />
              </Modal.Body>
            </form>
            {userIsAdmin ? (
              <>
                <hr />
                <form name="delete-form" style={{ marginBottom: "15px" }}>
                  <Modal.Body>
                    <h5>Delete</h5>
                    <p>
                      Please confirm the name of the Analysis you want to
                      delete.
                    </p>
                    <div>
                      <Form.Group controlId="analysis_delete">
                        <Form.Input
                          type="text"
                          placeholder="Confirm Analysis Name"
                          style={{ marginBottom: "25px" }}
                          value={deleteAnalysisConfirm || ""}
                          status={confirmAnalysisDelete() ? "valid" : "invalid"}
                          onChange={(e: any) => {
                            if (e.target instanceof HTMLInputElement) {
                              const value = e.target.value;
                              setDeleteAnalysisConfirm(value);
                            }
                          }}
                        />
                      </Form.Group>
                    </div>
                    <p>Are you sure you want to delete this Analysis?</p>
                    <p className="font-bold">
                      This action is permanent and irreversible.
                    </p>
                    <p>
                      The corresponding Power BI Workspace and all Reports will
                      also be deleted.
                    </p>
                    <input
                      type="submit"
                      value="Delete"
                      className={[
                        genStyles["alenza__btn-delete__mini"],
                        "float-right",
                      ].join(" ")}
                      disabled={!confirmAnalysisDelete()}
                      onClick={(e: any) => {
                        setIsDeletingAnalysis(true);
                        onHandleDeleteAnalysisClick(e);
                      }}
                    />
                  </Modal.Body>
                </form>
              </>
            ) : null}
          </>
        </Modal>
      </>
    );
  };

  return (
    <>
      {analysesCard()}
      {createAnalysisModal()}
      {analysisSettingsModal()}
      <ReactTooltip id="analyses-tooltip" />
    </>
  );
};

export default AnalysesCard;
